import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {useEffect, useMemo, useRef} from 'react'
import {useParams} from 'react-router-dom'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {checkEmptyString} from '../../utils/CommonUtils'
import {useAuth} from '../authentication/AuthProvider'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {usePatientCRUD} from '../patient/query-keys/PatientKeysManager'
import {
    getRefReloadEventKey,
    refReloadEvent,
    RefReloadEventTypes,
} from '../styled-component/information/RefReloadEvent'
import StyledDetailInformationTabView from '../styled-component/information/StyledDetailInformationTabView'
import {usePatientContext} from '../styled-component/information/StyleDetailInformationWrapper'

const Information = ({hideProfile = false}) => {
    useSignals()

    //#region ID
    const {id: idString} = useParams()
    const id = useMemo(() => {
        if (!idString) {
            return null
        }
        return parseInt(idString)
    }, [idString])
    //#endregion

    //#region Context
    const {user} = useAuth()
    const {currentViewedUser} = usePatientContext()

    const {handleCheckPermissionByKey} = usePatientContext()
    //#endregion

    //#region Query
    const queryClient = useQueryClient()

    const {patientKeys} = QueryKeyManager(QueryKeyTypes.patient, user?.id)

    const patientQueryKeys = useMemo(() => {
        return patientKeys.detail({id})
    }, [patientKeys, id])

    const {mutateAsync: createOrUpdatePatient} = usePatientCRUD(
        QueryCRUDTypes.update,
        user?.id,
    )
    //#endregion

    //#region Handle Function
    const handleSubmit = async data => {
        if (!data.hospitalName || typeof data.hospitalId !== 'number') {
            showNotification(
                severityNotification.error,
                'Hospital is required !!',
            )
            return
        }

        if (
            !data?.firstName ||
            data?.firstName?.length < 0 ||
            !!checkEmptyString(data?.firstName)
        ) {
            showNotification(
                severityNotification.error,
                'First Name is required !!',
            )
            return
        }

        let toPostData = {
            ...data,
            id: data?.id || null,
            byPassPathway: true,
            pathwayIds: data?.pathway,
        }

        const patientData = await createOrUpdatePatient(toPostData)

        if (patientData?.id) {
            const {generalPractitionerKeys} = QueryKeyManager(
                QueryKeyTypes.generalPractitioner,
                user?.id,
            )

            // queryClient.setQueryData(patientQueryKeys, () => patientData)
            queryClient.invalidateQueries(patientQueryKeys)
            queryClient.invalidateQueries(generalPractitionerKeys.pattern())
        }
    }
    //#endregion

    const visibleRef = useRef(null)
    const shouldVisible = useSignal(true)

    useEffect(() => {
        refReloadEvent.on(
            `${getRefReloadEventKey(
                RefReloadEventTypes.DETAIL_INFORMATION,
                id,
            )}`,
            () => {
                shouldVisible.value = false

                visibleRef.current = setTimeout(() => {
                    shouldVisible.value = true
                }, 1)
            },
        )

        return () => {
            refReloadEvent.removeListener(
                `${getRefReloadEventKey(
                    RefReloadEventTypes.DETAIL_INFORMATION,
                    id,
                )}`,
            )

            clearTimeout(visibleRef.current)
        }
    }, [])

    return (
        <div className="grid pt-5 p-0">
            <StyledDetailInformationTabView />
        </div>
    )
}

export default Information
